import { useCallback, useEffect, useState } from "react";
import { useIdContext } from "../../context/VariableContext";

const SKIP_TTL_MS = 24 * 60 * 60 * 1000;
const SKIP_PREFIX = "kit_skip_";

const readSkipExpiry = (idk) => {
    try {
        const raw = localStorage.getItem(SKIP_PREFIX + idk);
        if (!raw) return null;
        const { expiry } = JSON.parse(raw);
        if (typeof expiry !== "number" || Date.now() >= expiry) {
            localStorage.removeItem(SKIP_PREFIX + idk);
            return null;
        }
        return expiry;
    } catch {
        return null;
    }
};

const writeSkip = (idk) => {
    try {
        localStorage.setItem(
            SKIP_PREFIX + idk,
            JSON.stringify({ expiry: Date.now() + SKIP_TTL_MS })
        );
    } catch {
        // ignore
    }
};

/**
 * Si el usuario tiene kits pendientes (sin skip activo), renderiza el `fallback`
 * (típicamente la pantalla ResponsiveKitsV2). De lo contrario children.
 *
 * Soporta "Más tarde" con TTL de 24h por idk vía localStorage. El fallback
 * recibe `onSkip(idk)` para postergar el modal por ese idk.
 */
export const RequireNoPendingKits = ({ children, renderFallback }) => {
    const { userAsingKit, userOrdersLoaded, userAsingKitLoaded } = useIdContext();
    const [skipTick, setSkipTick] = useState(0);

    const handleSkip = useCallback((idk) => {
        if (!idk) return;
        writeSkip(idk);
        setSkipTick((t) => t + 1);
    }, []);

    // Forzar reevaluación cuando expira un skip mientras la sesión está abierta.
    useEffect(() => {
        const earringKits = (userAsingKit || []).filter(
            (k) => k.processed === "earring"
        );
        const expiries = earringKits
            .map((k) => readSkipExpiry(k.assignment?.id || k.idk))
            .filter(Boolean);
        if (expiries.length === 0) return;
        const minExpiry = Math.min(...expiries);
        const delay = Math.max(0, minExpiry - Date.now());
        const timer = setTimeout(() => setSkipTick((t) => t + 1), delay + 100);
        return () => clearTimeout(timer);
    }, [userAsingKit, skipTick]);

    if (!userOrdersLoaded || !userAsingKitLoaded) {
        return null;
    }

    const pendingKits = (userAsingKit || [])
        .filter((kit) => kit.processed === "earring")
        .filter((kit) => !readSkipExpiry(kit.assignment?.id || kit.idk))
        .sort((a, b) => a.timestamp - b.timestamp)[0];

    if (pendingKits) {
        return renderFallback ? renderFallback(pendingKits, handleSkip) : null;
    }

    return children;
};
